import { useContext, useEffect, useLayoutEffect, useRef } from "react";
import { Context } from "../redux/Context";

/**
 * Calls `onResync` each time the live connection comes back after a drop.
 *
 * Messages sent while the connection was down are lost - the hub does not replay them - so a
 * screen that shows live figures must reload them when it reconnects, or it keeps showing the
 * prices and timers it had before the drop (#182, #183). AuctionPlayer had this inline (#118); every
 * other live screen had nothing.
 *
 * Not called for the first connection, nor for one already open when the screen mounts: the screen
 * loads itself on mount. Called when ContextProvider reports a reconnect (`reconnectCount` rises)
 * and, defensively, when the connection object itself is replaced after one was seen.
 *
 * The callback is held in a ref, as in useHubEvent, so callers may pass a fresh inline arrow on
 * every render.
 *
 * @param {() => void} onResync Reloads the screen's live data. Should be quiet - no spinner - since
 *   the screen is already showing data.
 * @returns {void}
 */
export const useHubResync = (onResync) => {
  const { hubConnection, reconnectCount } = useContext(Context);
  const callbackRef = useRef(onResync);
  const seenRef = useRef({ hubConnection, reconnectCount });

  useLayoutEffect(() => {
    callbackRef.current = onResync;
  });

  useEffect(() => {
    const seen = seenRef.current;
    seenRef.current = { hubConnection, reconnectCount };

    const reconnected =
      (reconnectCount ?? 0) > (seen.reconnectCount ?? 0) ||
      (Boolean(seen.hubConnection) && Boolean(hubConnection) && hubConnection !== seen.hubConnection);

    if (reconnected) callbackRef.current?.();
  }, [hubConnection, reconnectCount]);
};
